import { FormType, ReportJSON, isDefined } from '@aloft/toolkit-shared';

import { ClickFormsAdjustmentGridFieldsMap, ClickFormsExportRow } from '../../types.ts';
import { fillInBlankRows } from '../../utils.ts';
import { generate1004LikeAdjustmentRows } from '../fnma1004/adjustments.ts';
import { generate1025LikeAdjustmentRows } from '../fnma1025/adjustments.ts';
import {
  ADD_COMPS_1004C_ADJUSTMENT_FIELDS,
  ADD_COMPS_1004C_FORM_CODE,
  ADD_COMPS_1004C_ROWS_PER_COMP,
  ADD_COMPS_1004C_TOTAL_ROWS,
  ADD_COMPS_1025_ADJUSTMENT_FIELDS,
  ADD_COMPS_1025_CODE,
  ADD_COMPS_1025_ROWS,
  ADD_COMPS_1025_ROWS_PER_COMP,
  ADD_COMPS_1025_TOTAL_ROWS,
  ADD_COMPS_ADJUSTMENT_FIELDS,
  ADD_COMPS_FORM_CODE,
  ADD_COMPS_ROWS,
  ADD_COMPS_ROWS_PER_COMP,
  ADD_COMPS_TOTAL_ROWS,
} from './consts.ts';

export function generateAdditionalCompsString(
  formType: FormType,
  compSequence: string[],
  { comps, meta }: ReportJSON,
  isMerge: boolean,
  index: number,
): string {
  let rows: ClickFormsExportRow[] = [];

  const { formCode, fieldMapping, rowsPerColumn, totalRows } = getAdditionalFormProps(formType);
  const HEADER_STRING = isMerge
    ? `CMD.MERGE.${formCode}.I${index}.BROADCAST\n`
    : `CMD.LOAD.${formCode}\n`;
  let result = HEADER_STRING;

  const subjectRows = generateSubjectRows(formType, meta);
  rows = rows.concat(subjectRows);

  compSequence.forEach((compID, index) => {
    const comp = comps[compID];

    if (comp) {
      switch (formType) {
        case FormType.fnma1025:
          rows = rows.concat(
            generate1025LikeAdjustmentRows({
              comp: comp.salesGridData,
              compIndex: index,
              fieldMapping,
              rowsPerColumn,
            }),
          );
          break;
        case FormType.fnma1004:
        case FormType.fnma1004c:
        case FormType.fnma2055:
          rows = rows.concat(
            generate1004LikeAdjustmentRows({
              comp: comp.salesGridData,
              compIndex: index,
              fieldMapping,
              rowsPerColumn,
            }),
          );
      }
    }
  });

  result = result + fillInBlankRows(rows, totalRows);

  return result;
}

function generateSubjectRows(formType: FormType, meta: ReportJSON['meta']): ClickFormsExportRow[] {
  const rows: ClickFormsExportRow[] = [];

  if (formType === FormType.fnma1025) {
    rows.push({ row: ADD_COMPS_1025_ROWS.SU_BLANK1_LABEL, value: 'Add. Features' });
    if (isDefined(meta?.otherFeature2Label)) {
      rows.push({
        row: ADD_COMPS_1025_ROWS.SU_BLANK2_LABEL,
        value: meta?.otherFeature2Label,
      });
    }
  } else {
    rows.push({ row: ADD_COMPS_ROWS.SU_BLANK1_LABEL, value: 'Add. Features' });
    if (isDefined(meta?.otherFeature2Label)) {
      rows.push({
        row: ADD_COMPS_ROWS.SU_BLANK2_LABEL,
        value: meta?.otherFeature2Label,
      });
    }
  }
  return rows;
}

function getAdditionalFormProps(formType: FormType): {
  formCode: string;
  fieldMapping: ClickFormsAdjustmentGridFieldsMap;
  rowsPerColumn: number;
  totalRows: number;
} {
  switch (formType) {
    case FormType.fnma1004:
    case FormType.fnma2055:
      return {
        formCode: ADD_COMPS_FORM_CODE,
        fieldMapping: ADD_COMPS_ADJUSTMENT_FIELDS,
        rowsPerColumn: ADD_COMPS_ROWS_PER_COMP,
        totalRows: ADD_COMPS_TOTAL_ROWS,
      };
    case FormType.fnma1004c:
      return {
        formCode: ADD_COMPS_1004C_FORM_CODE,
        fieldMapping: ADD_COMPS_1004C_ADJUSTMENT_FIELDS,
        rowsPerColumn: ADD_COMPS_1004C_ROWS_PER_COMP,
        totalRows: ADD_COMPS_1004C_TOTAL_ROWS,
      };
    case FormType.fnma1025:
      return {
        formCode: ADD_COMPS_1025_CODE,
        fieldMapping: ADD_COMPS_1025_ADJUSTMENT_FIELDS,
        rowsPerColumn: ADD_COMPS_1025_ROWS_PER_COMP,
        totalRows: ADD_COMPS_1025_TOTAL_ROWS,
      };
    default:
      throw new Error(`Form Type ${formType} not yet supported`);
  }
}
